import { useProjectStore, useFirmStore } from '@/stores'
import ApiService from './api.service'
import type {
  ProjectResponse,
  ProjectListResponse,
  ProjectPhotoListResponse,
  ProjectPhotoUploadResponse,
  ProjectPhotoDeleteResponse,
  ProjectSearchOptions,
  ProjectCreateData,
  ProjectUpdateData
} from '@/types/services'
import type {
  ProjectAuditLogResponseDto,
  ProjectAuditLogQueryParams,
  AuditLogPageResponseDto,
  ProjectStatusHistoryDto
} from '@/types/project/audit'

/**
 * Service for managing project-related API endpoints
 */
class ProjectService extends ApiService {
  constructor() {
    super({
      showNotifications: true,
      notificationOptions: {
        title: 'Project Error'
      }
    })
  }

  /**
   * Fetch projects with search and filtering parameters
   */
  async getProjects(options: ProjectSearchOptions = {}): Promise<ProjectListResponse | null> {
    const {
      page = 0,
      size = 10,
      sort = 'name,asc',
      filters = {},
      searchTerm = ''
    } = options

    // Build query parameters
    const params: Record<string, any> = { page, size, sort }
    
    // Add search term if provided
    if (searchTerm) {
      params.name = searchTerm
    }
    
    // Add all active filters
    Object.entries(filters).forEach(([key, value]) => {
      if (value) {
        params[key] = value
      }
    })
    
    // Call the search endpoint
    return this.get(`/api/project/mine`, params)
  }

  /**
   * Create a new project
   */
  async createProject(projectData: ProjectCreateData): Promise<ProjectResponse | null> {
    return this.post(`/api/project/`, projectData)
  }

  /**
   * Update an existing project
   */
  async updateProject(projectId: string, projectData: ProjectUpdateData): Promise<ProjectResponse | null> {
    if (!projectId) {
      return Promise.reject({ message: 'No project ID provided' })
    }
    
    return this.put(`/api/project/${projectId}`, projectData)
  }

  /**
   * Get a project by ID
   */
  async getProjectById(projectId: string): Promise<ProjectResponse | null> {
    if (!projectId) {
      return Promise.reject({ message: 'No project ID provided' })
    }
    
    return this.get(`/api/project/${projectId}`)
  }

  /**
   * Get photos for a specific project
   */
  async getProjectPhotos(projectId: string): Promise<ProjectPhotoListResponse | null> {
    if (!projectId) {
      return Promise.reject({ message: 'No project ID provided' })
    }
    
    return this.get(`/api/project/${projectId}/photos`)
  }

  /**
   * Upload photos to a project
   */
  async uploadProjectPhotos(
    projectId: string,
    formData: FormData,
    // Optional axios upload progress callback
    onUploadProgress?: (progressEvent: import('axios').AxiosProgressEvent) => void
  ): Promise<ProjectPhotoUploadResponse | null> {
    if (!projectId) {
      return Promise.reject({ message: 'No project ID provided' })
    }

    return this.post(`/api/project/${projectId}/photos/upload`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      },
      onUploadProgress
    })
  }

  /**
   * Delete a project photo
   */
  async deleteProjectPhoto(projectId: string, photoId: string): Promise<ProjectPhotoDeleteResponse | null> {
    if (!projectId || !photoId) {
      return Promise.reject({ message: 'Project ID and photo ID are required' })
    }
    
    return this.delete(`/api/project/${projectId}/photos/${photoId}`)
  }

  /**
   * Get all available project statuses
   */
  async getAllStatuses(): Promise<string[] | null> {
    return this.get<string[]>('/api/project/statuses');
  }

  /**
   * Get workflow order of project statuses for timeline display
   * Returns statuses in logical workflow order (main happy path)
   */
  async getWorkflowStatuses(): Promise<string[] | null> {
    return this.get<string[]>('/api/project/workflow-statuses');
  }

  /**
   * Get status timeline for a specific project
   * Returns the actual status history timeline for the project
   * @param projectId - The project ID
   * @returns Promise that resolves with list of status history entries
   */
  async getProjectStatusTimeline(projectId: string | number): Promise<ProjectStatusHistoryDto[] | null> {
    if (!projectId) {
      return Promise.reject({ message: 'No project ID provided' })
    }
    
    return this.get<ProjectStatusHistoryDto[]>(`/api/project/${projectId}/status-timeline`);
  }

  /**
   * Get possible status transitions for a project
   */
  async getPossibleStatuses(projectId: string): Promise<string[] | null> {
    if (!projectId) {
      return Promise.reject({ message: 'No project ID provided' })
    }
    
    return this.get<string[]>(`/api/project/${projectId}/possible-statuses`);
  }

  /**
   * Change project status
   */
  async changeProjectStatus(projectId: string, newStatus: string): Promise<ProjectResponse | null> {
    if (!projectId) {
      return Promise.reject({ message: 'No project ID provided' })
    }
    
    if (!newStatus) {
      return Promise.reject({ message: 'No status provided' })
    }
    
    return this.post(`/api/project/${projectId}/change-status?newStatus=${newStatus}`, {})
  }

  // ==================== AUDIT LOG METHODS ====================

  /**
   * Get audit logs for a project
   * @param projectId - The project ID
   * @param params - Query parameters for pagination and filtering
   * @returns Promise that resolves with paginated audit logs
   */
  async getProjectAuditLogs(
    projectId: number | string,
    params?: ProjectAuditLogQueryParams
  ): Promise<AuditLogPageResponseDto<ProjectAuditLogResponseDto> | null> {
    const queryString = this.buildAuditLogQueryString(params)
    return this.get(`/api/project/${projectId}/audit-logs${queryString}`)
  }

  /**
   * Build query string for audit log requests
   * @param params - The query parameters
   * @returns Query string
   */
  private buildAuditLogQueryString(params?: ProjectAuditLogQueryParams): string {
    if (!params) return ''
    
    const searchParams = new URLSearchParams()
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        searchParams.append(key, String(value))
      }
    })
    
    const queryString = searchParams.toString()
    return queryString ? `?${queryString}` : ''
  }
}

// Create and export a singleton instance
export default new ProjectService()
